import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, CircleDashed, X, Rocket } from "lucide-react";

const STORAGE_KEY = "out-and-in.onboarding.dismissed";

// Deriving progress purely from data means we don't need extra backend state.
export default function OnboardingChecklist() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(STORAGE_KEY) === "1";
  });
  const [steps, setSteps] = useState(null);

  const load = useCallback(async () => {
    try {
      const [meRes, reqsRes, couriersRes, invitesRes] = await Promise.all([
        api.get("/auth/me"),
        api.get("/requests"),
        api.get("/couriers"),
        api.get("/team/invites").catch(() => ({ data: [] })),
      ]);
      const me = meRes.data || {};
      const requests = reqsRes.data || [];
      const couriers = couriersRes.data || [];
      const invites = invitesRes.data || [];

      const s = [
        {
          id: "profile",
          title: "Complete your company profile",
          desc: "Add your company name, address and logo so every email is on‑brand.",
          done: Boolean(me.company_name && me.company_address),
          to: "/company",
          cta: "Open profile",
        },
        {
          id: "courier",
          title: "Save your first courier",
          desc: "Add the couriers you regularly appoint to speed up every future request.",
          done: couriers.length > 0,
          to: "/couriers",
          cta: "Add courier",
        },
        {
          id: "request",
          title: "Log your first request",
          desc: "Try a pickup or delivery — the supplier and courier will each get a notification.",
          done: requests.length > 0,
          to: "/requests/new",
          cta: "New request",
        },
        {
          id: "invite",
          title: "Invite a teammate",
          desc: "Bring your ops team in so everyone can dispatch and receive together.",
          done: invites.length > 0,
          to: "/company",
          cta: "Invite teammate",
        },
      ];
      setSteps(s);
    } catch {
      setSteps([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (dismissed || !steps || steps.length === 0) return null;
  const doneCount = steps.filter((s) => s.done).length;
  if (doneCount === steps.length) return null; // auto-hide when complete

  const percent = Math.round((doneCount / steps.length) * 100);
  const nextStep = steps.find((s) => !s.done);

  return (
    <section
      data-testid="onboarding-checklist"
      className="border-2 border-orange-600 bg-orange-50 dark:bg-orange-950/40 p-4 sm:p-6"
    >
      <div className="flex items-start gap-3">
        <div className="border-2 border-orange-600 bg-orange-600 text-white p-2 shrink-0">
          <Rocket className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-orange-700 dark:text-orange-300">Get Started</div>
              <h2 className="font-heading text-xl sm:text-2xl font-black tracking-tight mt-0.5">
                {doneCount === 0 ? "Welcome — 4 quick steps to be dispatch‑ready" : `${doneCount} of ${steps.length} done — keep going`}
              </h2>
            </div>
            <button
              type="button"
              onClick={() => { localStorage.setItem(STORAGE_KEY, "1"); setDismissed(true); }}
              data-testid="onboarding-dismiss"
              className="text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground hover:text-foreground flex items-center gap-1"
              aria-label="Dismiss onboarding checklist"
            >
              <X className="h-3.5 w-3.5" /> Hide
            </button>
          </div>

          <div className="h-1.5 bg-orange-200 dark:bg-orange-900 mt-4 mb-4 overflow-hidden">
            <div
              data-testid="onboarding-progress"
              className="h-full bg-orange-600 transition-all duration-500"
              style={{ width: `${percent}%` }}
            />
          </div>

          <ol className="space-y-2">
            {steps.map((s, i) => (
              <li
                key={s.id}
                data-testid={`onboarding-step-${s.id}${s.done ? "-done" : ""}`}
                className={`flex items-center gap-3 border-2 ${s.done ? "border-emerald-600 bg-emerald-50/60 dark:bg-emerald-950/30" : (s.id === nextStep?.id ? "border-orange-600 bg-white dark:bg-black" : "border-border bg-white/60 dark:bg-black/30")} px-3 py-2`}
              >
                <div className={`h-6 w-6 flex items-center justify-center border-2 shrink-0 ${s.done ? "border-emerald-600 bg-emerald-600 text-white" : "border-muted-foreground text-muted-foreground"}`}>
                  {s.done ? <Check className="h-3.5 w-3.5" /> : <CircleDashed className="h-3.5 w-3.5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-bold ${s.done ? "line-through text-muted-foreground" : ""}`}>
                    <span className="font-mono text-xs text-muted-foreground mr-2">{String(i + 1).padStart(2, "0")}</span>
                    {s.title}
                  </div>
                  {!s.done && <div className="text-xs text-muted-foreground hidden sm:block">{s.desc}</div>}
                </div>
                {!s.done && (
                  <Link
                    to={s.to}
                    onClick={load}
                    data-testid={`onboarding-cta-${s.id}`}
                    className={`text-[11px] uppercase tracking-[0.15em] font-bold px-3 py-1.5 border-2 whitespace-nowrap ${s.id === nextStep?.id ? "border-orange-600 bg-orange-600 text-white hover:bg-orange-700" : "border-border hover:border-orange-600"}`}
                  >
                    {s.cta}
                  </Link>
                )}
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}
